import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env['VITE_LICENSE_API_URL'] ?? 'https://license.chikchaklabs.com';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Request failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ─── Types ───

export interface CustomerProfile {
  customerId: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  customCss?: string;
  status: string;
  createdAt: string;
}

export interface License {
  apiKey: string;
  siteId: string;
  plugins: string[];
  domains: string[];
  maxDomains: number;
  features: string[];
  expiresAt: string;
  isActive: boolean;
  plan: string;
  createdAt: string;
}

// ─── API Methods ───

export async function signup(data: { name: string; company?: string; plugin: string }) {
  return request<{ customer: CustomerProfile; license: License }>('/portal/signup', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getMe() {
  return request<{ customer: CustomerProfile; licenses: License[] }>('/portal/me');
}

export async function updateMe(data: { name?: string; phone?: string; company?: string }) {
  return request<CustomerProfile>('/portal/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateCss(customCss: string) {
  return request<CustomerProfile>('/portal/css', {
    method: 'PUT',
    body: JSON.stringify({ customCss }),
  });
}

export async function listLicenses() {
  return request<{ licenses: License[] }>('/portal/licenses');
}

export async function addPlugin(plugin: string) {
  return request<{ customer: CustomerProfile; license: License }>('/portal/signup', {
    method: 'POST',
    body: JSON.stringify({ name: '_existing', plugin }),
  });
}
